import { useState } from 'react';
import { useTranslation } from 'react-i18next';

export default function Solutions() {
  const { t } = useTranslation('home');
  const [selectedSolution, setSelectedSolution] = useState<number | null>(null);

  const solutions = [
    {
      icon: 'ri-settings-3-line',
      title: t('solutions.items.0.title'),
      subtitle: t('solutions.items.0.subtitle'),
      description: t('solutions.items.0.description'),
      detail: t('solutions.items.0.detail'),
      color: 'from-orange-500 to-orange-600',
      iconBg: 'bg-orange-500',
      fullDescription: t('solutions.items.0.fullDescription'),
      features: [
        t('solutions.items.0.features.0'),
        t('solutions.items.0.features.1'),
        t('solutions.items.0.features.2'),
        t('solutions.items.0.features.3'),
        t('solutions.items.0.features.4')
      ]
    },
    {
      icon: 'ri-shield-check-line',
      title: t('solutions.items.1.title'),
      subtitle: t('solutions.items.1.subtitle'),
      description: t('solutions.items.1.description'),
      detail: t('solutions.items.1.detail'),
      color: 'from-lime-500 to-lime-600',
      iconBg: 'bg-lime-500',
      fullDescription: t('solutions.items.1.fullDescription'),
      features: [
        t('solutions.items.1.features.0'),
        t('solutions.items.1.features.1'),
        t('solutions.items.1.features.2'),
        t('solutions.items.1.features.3'),
        t('solutions.items.1.features.4')
      ]
    },
    {
      icon: 'ri-building-line',
      title: t('solutions.items.2.title'),
      subtitle: t('solutions.items.2.subtitle'),
      description: t('solutions.items.2.description'),
      detail: t('solutions.items.2.detail'),
      color: 'from-cyan-500 to-cyan-600',
      iconBg: 'bg-cyan-500',
      fullDescription: t('solutions.items.2.fullDescription'),
      features: [
        t('solutions.items.2.features.0'),
        t('solutions.items.2.features.1'),
        t('solutions.items.2.features.2'),
        t('solutions.items.2.features.3'),
        t('solutions.items.2.features.4')
      ]
    },
    {
      icon: 'ri-archive-line',
      title: t('solutions.items.3.title'),
      subtitle: t('solutions.items.3.subtitle'),
      description: t('solutions.items.3.description'),
      detail: t('solutions.items.3.detail'),
      color: 'from-emerald-500 to-emerald-600',
      iconBg: 'bg-emerald-500',
      fullDescription: t('solutions.items.3.fullDescription'),
      features: [
        t('solutions.items.3.features.0'),
        t('solutions.items.3.features.1'),
        t('solutions.items.3.features.2'),
        t('solutions.items.3.features.3'),
        t('solutions.items.3.features.4')
      ]
    }
  ];

  const handleCloseModal = () => {
    setSelectedSolution(null);
  };

  return (
    <section id="products" className="py-20 bg-gradient-to-b from-white to-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Title */}
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">{t('solutions.title')}</h2>
          <p className="text-lg text-gray-600">{t('solutions.subtitle')}</p>
        </div>

        {/* Solutions Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {solutions.map((solution, index) => (
            <div
              key={index}
              className="group relative bg-white rounded-lg overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 flex flex-col"
            >
              {/* Icon */}
              <div className="relative pt-12 pb-6 px-6 bg-gray-50 flex-grow flex flex-col">
                <div className={`w-16 h-16 flex items-center justify-center ${solution.iconBg} rounded-full mx-auto mb-4 shadow-lg`}>
                  <i className={`${solution.icon} text-3xl text-white`}></i>
                </div>
                
                {/* Content */}
                <div className="text-center flex-grow flex flex-col justify-center min-h-[120px]">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{solution.title}</h3>
                  <p className="text-sm text-gray-600 mb-3">{solution.subtitle}</p>
                  <p className="text-sm text-gray-700 mb-1">{solution.description}</p>
                  {solution.detail && (
                    <p className="text-sm text-gray-700">{solution.detail}</p>
                  )}
                </div>
              </div>

              {/* Button */}
              <div 
                className={`bg-gradient-to-r ${solution.color} py-4 px-6 text-center cursor-pointer hover:opacity-90 transition-opacity`}
                onClick={() => setSelectedSolution(index)}
              >
                <span className="text-white font-medium whitespace-nowrap">{t('solutions.viewDetails')}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Modal */}
      {selectedSolution !== null && (
        <div 
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          onClick={handleCloseModal}
        >
          <div 
            className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className={`bg-gradient-to-r ${solutions[selectedSolution].color} px-8 py-6 relative`}>
              <button
                onClick={handleCloseModal}
                className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center bg-white/20 hover:bg-white/30 rounded-full transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-2xl text-white"></i>
              </button>
              <div className="flex items-center gap-4">
                <div className={`w-16 h-16 flex items-center justify-center bg-white/20 rounded-full`}>
                  <i className={`${solutions[selectedSolution].icon} text-3xl text-white`}></i>
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-white mb-1">{solutions[selectedSolution].title}</h3>
                  <p className="text-sm text-white/90">{solutions[selectedSolution].subtitle}</p>
                </div>
              </div>
            </div>

            {/* Modal Content */}
            <div className="px-8 py-6">
              <div className="mb-6">
                <h4 className="text-lg font-bold text-gray-900 mb-3">{t('solutions.productIntro')}</h4>
                <p className="text-gray-700 leading-relaxed">{solutions[selectedSolution].fullDescription}</p>
              </div>

              <div>
                <h4 className="text-lg font-bold text-gray-900 mb-3">{t('solutions.coreFeatures')}</h4>
                <ul className="space-y-3">
                  {solutions[selectedSolution].features.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      <div className={`w-6 h-6 flex items-center justify-center ${solutions[selectedSolution].iconBg} rounded-full mt-0.5 flex-shrink-0`}>
                        <i className="ri-check-line text-sm text-white"></i>
                      </div>
                      <span className="text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="px-8 py-6 bg-gray-50 border-t border-gray-200">
              <button
                onClick={handleCloseModal}
                className="w-full py-3 bg-gray-900 hover:bg-gray-800 text-white font-medium rounded-lg transition-colors cursor-pointer whitespace-nowrap"
              >
                {t('solutions.close')}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
